<template lang="pug">
div(:class="{ 'has-data': inventories.length > 0 }")
  //- Mobile card list view
  template(v-if="isMobile")
    //- Loading skeleton
    template(v-if="loading")
      v-card.rounded-lg.mb-3(v-for="n in 3" :key="'skeleton-' + n" flat outlined)
        .pa-4
          v-skeleton-loader(type="list-item-three-line")

    //- Empty state
    .text-center.py-8(v-else-if="!inventories.length")
      .yz-regular-body-sm.neutral500--text No items found

    //- Card list
    template(v-else)
      v-card.rounded-lg.mb-3(v-for="(item, index) in inventories" :key="item.id" flat outlined @click="onEditItem(item)")
        .pa-4
          //- Header: Name + HQ badge + 3-dot menu
          .d-flex.align-center.justify-space-between
            .d-flex.align-center.justify-space-between.flex-grow-1(style="min-width: 0;")
              .yz-medium-body-sm.neutral700--text.text-truncate {{ item.product ? item.product.name : item.name }}
              yz-badge(x-small color="blue-100" text-color="blue-500" border) HQ
            v-menu(offset-y left rounded="lg")
              template(#activator="{ on }")
                yz-btn(icon-only icon="$yzi-dots-horizontal" icon-size="20" size="sm" v-on="on" @click.native.stop)
              yz-list.py-0
                yz-list-item(@click="onEditItem(item)")
                  div Edit item
                yz-divider
                yz-list-item(@click="onStockHistory(item)")
                  div Stock history
                template(v-if="item.product && item.product.reserved_qty > 0")
                  yz-divider
                  yz-list-item(@click="onViewReservation")
                    div View reservation
                yz-divider
                yz-list-item(@click="onArchiveItem(item)")
                  div(:class="item.product && item.product.status === 'published' ? 'danger500--text' : 'success500--text'") {{ item.product && item.product.status === 'published' ? 'Archive' : 'Activate' }}

          //- Category
          .d-flex.align-center.mt-3(v-if="item.category")
            .yz-regular-body-xs.neutral500--text.mr-2 Category
            .yz-regular-body-xs.neutral700--text.text-capitalize {{ item.category }}

          //- Groups
          .d-flex.align-center.mt-2(v-if="item.groups && item.groups.length")
            .yz-regular-body-xs.neutral500--text.mr-2 Groups
            yz-truncated-list(:items="item.groups" text-item="value" :gap="4")

          //- Cost & Price
          .mt-2(v-if="(item.cost_range && item.cost_range.length) || (item.price_range && item.price_range.length)")
            .yz-regular-body-xs.success500--text(v-if="item.cost_range && item.cost_range.length") Cost: {{ item.cost_range[0] | displayPrice($currency) }}
              span(v-if="item.cost_range.length > 1") &nbsp;- {{ item.cost_range[1] | displayPrice($currency) }}
            .yz-regular-body-xs.success500--text(v-if="item.price_range && item.price_range.length") Price: {{ item.price_range[0] | displayPrice($currency) }}
              span(v-if="item.price_range.length > 1") &nbsp;- {{ item.price_range[1] | displayPrice($currency) }}

          //- Stock
          .d-flex.align-center.mt-2(v-if="item.product")
            .yz-regular-body-xs.neutral500--text.mr-2 {{ activeTab === 2 ? 'Reserved' : 'Stock' }}
            .yz-medium-body-sm.text-blue-500.mr-2 {{ activeTab === 2 ? item.product.reserved_qty : item.product.inventory_qty }}
            .yz-regular-body-xs.neutral500--text(v-if="activeTab === 2") On hand: {{ item.product.inventory_qty || 0 }} | Available: {{ item.product.available_qty || 0 }} | Committed: {{ item.product.committed_qty || 0 }} | Incoming: {{ item.product.preorder_qty || 0 }} | To order: {{ item.product.to_order_qty || 0 }}
            .yz-regular-body-xs.neutral500--text(v-else) On hand: {{ item.product.inventory_qty || 0 }} | Available: {{ item.product.available_qty || 0 }}

          //- Expiry date
          .d-flex.align-center.mt-2(v-if="item.nearest_expiry_date || item.latest_expiry_date")
            .yz-regular-body-xs.neutral500--text.mr-2 Expiry
            .yz-regular-body-xs.neutral700--text
              span(v-if="item.nearest_expiry_date") Nearest: {{ item.nearest_expiry_date | humanizeDate }}
              span(v-if="item.nearest_expiry_date && item.latest_expiry_date") &nbsp;|&nbsp;
              span(v-if="item.latest_expiry_date") Latest: {{ item.latest_expiry_date | humanizeDate }}

          //- Location
          .d-flex.align-center.mt-2(v-if="item.assigned_branches && item.assigned_branches.length")
            .yz-regular-body-xs.neutral500--text.mr-2 Location
            yz-truncated-list(:items="item.assigned_branches" text-item="name" :gap="4")

  //- Desktop table view
  .table-scroll-wrapper(v-else)
    yz-data-table(:headers="tableHeaders" :items="inventories" item-key="id" :loading="loading" :server-items-length="totalCount" no-outer-border radius="0" disable-sort body-height="52px" header-height="36px" header-class="yz-bold-body-xs" hide-default-footer :items-per-page="pageSize" @click:row="onEditItem")
      //- TODO: uncomment when multi-selection is implemented
      //- Select all checkbox
      //- template(v-slot:header.data-table-select)
      //-   yz-checkbox(:value="allSelected" :indeterminate="someSelected" @change="onToggleSelectAll" @click.native.stop)
      //- Individual row checkbox
      //- template(v-slot:item.data-table-select="{ item }")
      //-   yz-checkbox(:value="isSelected(item.id)" @change="onToggleSelect(item)" @click.native.stop)
      //- Name
      template(v-slot:item.name="{ item }")
        .d-flex.align-center.justify-space-between
          .yz-medium-body-sm.neutral700--text.mr-2 {{ item.product ? item.product.name : item.name }}
          yz-badge(x-small color="blue-100" text-color="blue-500" border) HQ
      //- Category
      template(v-slot:item.category="{ item }")
        .yz-regular-body-sm.neutral700--text.text-capitalize {{ item.category }}
      //- Groups
      template(v-slot:item.groups="{ item }")
        yz-truncated-list.my-3(v-if="item.groups && item.groups.length" :items="item.groups" text-item="value" :gap="4")
      //- Cost & Price
      template(v-slot:item.costPrice="{ item }")
        .my-3
          .yz-regular-body-xs.success500--text(v-if="item.cost_range && item.cost_range.length") Cost: {{ item.cost_range[0] | displayPrice($currency) }}
            span(v-if="item.cost_range.length > 1") &nbsp;- {{ item.cost_range[1] | displayPrice($currency) }}
          .yz-regular-body-xs.success500--text(v-if="item.price_range && item.price_range.length") Price: {{ item.price_range[0] | displayPrice($currency) }}
            span(v-if="item.price_range.length > 1") &nbsp;- {{ item.price_range[1] | displayPrice($currency) }}
      //- Stock
      template(v-slot:item.stock="{ item }")
        .d-flex.align-center.justify-space-between
          .yz-medium-body-sm.text-blue-500 {{ activeTab === 2 ? item.product.reserved_qty : item.product.inventory_qty }}
          yz-tooltip
            template(#tooltip)
              template
                div On hand: {{ item.product.inventory_qty || 0 }}
                div Available: {{ item.product.available_qty || 0 }}
                div Committed: {{ item.product.committed_qty || 0 }}
                div Incoming: {{ item.product.preorder_qty || 0 }}
                div To order: {{ item.product.to_order_qty || 0 }}
            yz-icon(size="14" color="neutral500") $yzi-info-circle
      //- Expiry date
      template(v-slot:item.expiryDate="{ item }")
        div(v-if="item.nearest_expiry_date || item.latest_expiry_date")
          .yz-regular-body-xs.neutral700--text(v-if="item.nearest_expiry_date") Nearest: {{ item.nearest_expiry_date | humanizeDate }}
          .yz-regular-body-xs.neutral700--text(v-if="item.latest_expiry_date") Latest: {{ item.latest_expiry_date | humanizeDate }}
      //- Location
      template(v-slot:item.location="{ item }")
        yz-truncated-list(v-if="item.assigned_branches && item.assigned_branches.length" :items="item.assigned_branches" text-item="name" :gap="4")
      //- Actions
      template(v-slot:item.actions="{ item, index }")
        .d-flex.align-center.justify-end(@click.stop)
          yz-btn.mr-1(icon-only, icon-color="neutral-500" icon="$yzi-package-plus" icon-size="22" size="lg" @click.stop="onUpdateStock(item)")
          v-menu(offset-y :top="menuPositionTop(index)" left rounded="lg")
            template(#activator="{ on }")
              yz-btn(icon-only icon="$yzi-dots-horizontal" icon-size="20" size="lg" v-on="on")
            yz-list.py-0
              yz-list-item(@click="onEditItem(item)")
                div Edit item
              yz-divider
              yz-list-item(@click="onStockHistory(item)")
                div Stock history
              template(v-if="item.product && item.product.reserved_qty > 0")
                yz-divider
                yz-list-item(@click="onViewReservation(item)")
                  div View reservation
              yz-divider
              yz-list-item(@click="onArchiveItem(item)")
                div(:class="item.product && item.product.status === 'published' ? 'danger500--text' : 'success500--text'") {{ item.product && item.product.status === 'published' ? 'Archive' : 'Activate' }}

  //- Pagination
  .d-flex.align-center.mt-4(v-if="totalCount > pageSize")
    v-spacer
    yz-pagination(v-model="currentPage" version="yc" :page-size.sync="pageSize" :count="totalCount" @input="onPageChanged")

  //- TODO: uncomment when multi-selection is implemented
  //- inventory-selection-actions(v-if="!isMobile" :selected-items="selectedItems" :show="selectedItems.length > 0" @assign="onAssignBranch" @request-quotation="$emit('request-quotation', selectedItems)" @create-po="$emit('create-po', selectedItems)")

  //- Update stock dialog
  update-stock-dialog(v-if="showUpdateStockDialog" :open.sync="showUpdateStockDialog" :item="selectedItemForStockUpdate" @closed="showUpdateStockDialog = false" @updated="onStockUpdated")

  //- Assign branch & warehouse dialog
  assign-branch-warehouse-dialog(v-if="showAssignDialog" :open.sync="showAssignDialog" :items="selectedItemsForAssign" @closed="showAssignDialog = false" @updated="onAssignUpdated")

  //- Archive warning dialog
  archive-inventory-warning-dialog(v-if="openArchiveWarningDialog" v-model="openArchiveWarningDialog" :type="archiveWarningType")

  //- View reservation dialog
  view-reservation-dialog(v-if="showViewReservationDialog" :open.sync="showViewReservationDialog" :medication="selectedMedicationForReservation")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import InventorySelectionActions from './InventorySelectionActions.vue'
import UpdateStockDialog from './UpdateStockDialog.vue'
import AssignBranchWarehouseDialog from './AssignBranchWarehouseDialog.vue'
import ArchiveInventoryWarningDialog from '@/components/clinic/inventory/ArchiveInventoryWarningDialog.vue'
import ViewReservationDialog from '@/components/clinic/inventory/ViewReservationDialog.vue'

export default {
  components: { InventorySelectionActions, UpdateStockDialog, AssignBranchWarehouseDialog, ArchiveInventoryWarningDialog, ViewReservationDialog },
  props: {
    isMobile: { type: Boolean, default: false },
    activeTab: { type: Number, default: 0 },
    searchText: { type: String, default: '' },
    filters: { type: Array, default: () => [] },
    locationFilter: { default: null }
  },
  data: () => ({
    loading: false,
    inventories: [],
    totalCount: 0,
    currentPage: 1,
    pageSize: 20,
    selectedItems: [],
    showUpdateStockDialog: false,
    selectedItemForStockUpdate: null,
    showAssignDialog: false,
    selectedItemsForAssign: [],
    archiveLoading: false,
    openArchiveWarningDialog: false,
    archiveWarningType: 'reservation',
    showViewReservationDialog: false,
    selectedMedicationForReservation: null
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      hqInventoryFields: 'hq/getInventoryFields'
    }),
    tableHeaders () {
      // TODO: uncomment when multi-selection is implemented
      // const select = { text: '', value: 'data-table-select', sortable: false, width: 50 }
      const name = { text: 'NAME', value: 'name', sortable: false }
      const fields = this.hqInventoryFields.filter(item => item.enable).map(item => ({
        text: item.id === 'stock' && this.activeTab === 2 ? 'Reserved' : item.field_name,
        value: item.id,
        sortable: false,
        ...(item.width ? { width: item.width } : {})
      }))
      const actions = { text: '', value: 'actions', sortable: false, width: 100 }
      return [name, ...fields, actions]
    },
    allSelected () {
      return this.inventories.length > 0 && this.selectedItems.length === this.inventories.length
    },
    someSelected () {
      return this.selectedItems.length > 0 && !this.allSelected
    }
  },
  watch: {
    activeTab () {
      this.currentPage = 1
      this.selectedItems = []
      this.fetchMedications()
    },
    filters () {
      this.currentPage = 1
      this.selectedItems = []
      this.fetchMedications()
    },
    locationFilter () {
      this.currentPage = 1
      this.fetchMedications()
    }
  },
  mounted () {
    this.fetchMedications()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchMedications () {
      this.loading = true
      const params = {
        page: this.currentPage,
        page_size: this.pageSize,
        status: 'published'
      }
      // Tab filters
      if (this.activeTab === 1) params.stock__gt = 0
      if (this.activeTab === 2) params.reserved = true
      if (this.activeTab === 3) params.low_stock = true
      if (this.activeTab === 4) params.stock__lte = 0
      if (this.activeTab === 5) params.expire_soon = true
      if (this.activeTab === 6) params.status = 'draft'
      // Search
      if (this.searchText) params.search = this.searchText
      // Filters
      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })
      // Location filter — null means all (skip param), array means partial selection
      if (this.locationFilter) params.branches = this.locationFilter.join(',')

      this.$axios.get(`/clinics/${this.company.slug}/hq/medications`, { params })
        .then(resp => {
          this.inventories = resp.data.results || []
          this.totalCount = resp.data.count || 0
        })
        .catch(err => {
          const message = err.response?.data?.detail
          if (message && message.includes('Invalid page')) {
            this.currentPage = 1
            this.fetchMedications()
          } else {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
            this.inventories = []
            this.totalCount = 0
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    isSelected (id) {
      return this.selectedItems.some(item => item.id === id)
    },
    onToggleSelect (item) {
      if (this.isSelected(item.id)) {
        this.selectedItems = this.selectedItems.filter(i => i.id !== item.id)
      } else {
        this.selectedItems.push(item)
      }
    },
    onToggleSelectAll (value) {
      if (value) {
        this.selectedItems = [...this.inventories]
      } else {
        this.selectedItems = []
      }
    },
    onPageChanged () {
      this.fetchMedications()
    },
    onViewItem (item) {
      this.$emit('view-item', item)
    },
    onEditItem (item) {
      this.$emit('edit-item', item)
    },
    onUpdateStock (item) {
      this.selectedItemForStockUpdate = item
      this.showUpdateStockDialog = true
    },
    onStockUpdated () {
      this.showUpdateStockDialog = false
      this.fetchMedications()
    },
    onAssignBranch () {
      this.selectedItemsForAssign = [...this.selectedItems]
      this.showAssignDialog = true
    },
    onAssignUpdated () {
      this.showAssignDialog = false
      this.selectedItems = []
      this.fetchMedications()
    },
    onStockHistory (item) {
      this.$emit('stock-history', item)
    },
    onViewReservation (item) {
      this.selectedMedicationForReservation = item
      this.showViewReservationDialog = true
    },
    onArchiveItem (item) {
      if (item.product && item.product.status === 'published') {
        if (item.product.reserved_qty > 0) {
          this.archiveWarningType = 'reservation'
          this.openArchiveWarningDialog = true
          return
        }
        if (item.product.preorder_qty > 0) {
          this.archiveWarningType = 'preorder'
          this.openArchiveWarningDialog = true
          return
        }
      }
      this.archiveLoading = true
      const data = {
        product: {
          status: item.product && item.product.status === 'published' ? 'draft' : 'published'
        }
      }
      this.$axios.patch(`/clinics/${this.company.slug}/hq/medications/${item.id}`, data)
        .then(() => {
          this.showMessage(['success', item.product && item.product.status === 'published' ? 'Item archived' : 'Item activated'])
          this.fetchMedications()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.archiveLoading = false
        })
    },
    menuPositionTop (index) {
      const position = index + 1
      if (position > 7) {
        const start = this.inventories.length - 3
        if (position > start) return true
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
.has-data ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  cursor: pointer;
  background: var(--yz-neutral-200) !important;
}
.table-scroll-wrapper {
  overflow-x: auto;
}
.table-scroll-wrapper ::v-deep .yz-data-table {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper > table {
  min-width: 1400px;
}
</style>
